if (!customElements.get('quick-order-list-sort')) {
  customElements.define(
    'quick-order-list-sort',
    class QuickOrderListSort extends HTMLElement {
      constructor() {
        super();
        this.applySort = this.applySort.bind(this);
        this.onChange = this.onChange.bind(this);
        // Natural, number-aware compare so "US 3" sorts before "US 10"
        // and 9" stays before 12".
        this.collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
        this.watching = false;
      }

      // [L] and [S] are a size-class prefix on lace tips ("[L] US 9", "[S] US 2").
      // Comparing them as written groups every [L] before every [S].
      variantSortKey(value) {
        return String(value || '')
          .replace(/^(?:\s*\[(?:l|s)\]\s*)+/i, '')
          .trim();
      }

      connectedCallback() {
        this.select = this.querySelector('[data-sort-select]');
        if (!this.select) return;

        // Restore the shopper's choice for this product (survives pagination,
        // which rebuilds the whole <quick-order-list> and this element with it).
        const stored = this.storageKey && sessionStorage.getItem(this.storageKey);
        if (stored) {
          const option = this.select.querySelector(`option[value="${stored}"]`);
          if (option) this.select.value = stored;
        }

        this.select.addEventListener('change', this.onChange);

        // Sort once now, then keep the order in sync every time
        // quick-order-list.js re-renders the table (quantity edits, ajax).
        this.applySort();
        this.startObserving();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.watching = false;
      }

      get storageKey() {
        const id = this.dataset.productId;
        return id ? `qol-sort-${id}` : null;
      }

      get list() {
        return this.closest('quick-order-list');
      }

      get table() {
        return this.list?.querySelector('.quick-order-list__table');
      }

      get tbody() {
        return this.table?.querySelector('tbody');
      }

      onChange() {
        if (this.storageKey) sessionStorage.setItem(this.storageKey, this.select.value);
        this.applySort();
      }

      startObserving() {
        const table = this.table;
        if (!table) return;
        if (!this.observer) {
          this.observer = new MutationObserver(this.applySort);
        }
        // Watch the <table>: on re-render its innerHTML (thead + tbody) is
        // swapped, which shows up as a childList change here.
        this.observer.observe(table, { childList: true });
        this.watching = true;
      }

      applySort() {
        const tbody = this.tbody;
        if (!tbody || !this.select) return;

        const [, direction] = this.select.value.split('-');
        const factor = direction === 'desc' ? -1 : 1;

        // Each variant is a group: its main row plus the sibling rows that
        // trail it (the hidden desktop error row) until the next variant row.
        // Keeping them grouped means the error row always follows its variant.
        const groups = [];
        let current = null;
        Array.from(tbody.children).forEach((el) => {
          if (el.classList && el.classList.contains('variant-item')) {
            current = {
              key: this.variantSortKey(el.dataset.sortName || el.textContent || ''),
              els: [el],
              lead: false,
            };
            groups.push(current);
          } else if (current) {
            current.els.push(el);
          } else {
            // Anything before the first variant row: leave it up top.
            groups.push({ key: '', els: [el], lead: true });
          }
        });

        const lead = groups.filter((group) => group.lead);
        const sortable = groups.filter((group) => !group.lead);
        if (sortable.length < 2) return;

        sortable.sort((a, b) => factor * this.collator.compare(a.key, b.key));

        // Guard our own DOM writes so they don't retrigger the observer.
        const wasWatching = this.watching;
        if (wasWatching) {
          this.observer.disconnect();
          this.watching = false;
        }

        [...lead, ...sortable].forEach((group) => {
          group.els.forEach((el) => tbody.appendChild(el));
        });

        // Keep keyboard arrow-nav between quantity inputs in visual order.
        const list = this.list;
        if (list && Array.isArray(list.allInputsArray)) {
          list.allInputsArray = Array.from(list.querySelectorAll('input[type="number"]'));
        }

        if (wasWatching) this.startObserving();
      }
    }
  );
}
